package gr.tech.site.components.sections

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.functions.LinearGradient
import com.varabyte.kobweb.compose.css.functions.invert
import com.varabyte.kobweb.compose.css.functions.linearGradient
import com.varabyte.kobweb.compose.css.functions.toImage
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.selectors.after
import com.varabyte.kobweb.silk.style.selectors.before
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.site.util.Constants
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img

// Note - Styles
val LogosSectionStyle = CssStyle {
    base {
        Modifier
            .padding(topBottom = 60.px)
            .whiteSpace(WhiteSpace.NoWrap)
            .position(Position.Relative)
            .overflow(Overflow.Hidden)

    }
    cssRule(":hover div") {
        Modifier
            .styleModifier {
                property("animation-play-state", "paused")
            }
    }
    before {
        Modifier
            .position(Position.Absolute)
            .top(0.px)
            .left(0.px)
            .width(125.px)
            .height(100.percent)
            .content("")
            .zIndex(2)
            .background(
                Background.of (
                    image = linearGradient(
                        from = rgba(255, 255, 255, 0),
                        to = Colors.White,
                        dir = LinearGradient.Direction.ToLeft,
                    ).toImage()
                )
            )
    }
    after {
        Modifier
            .position(Position.Absolute)
            .top(0.px)
            .right(0.px)
            .width(125.px)
            .height(100.percent)
            .content("")
            .zIndex(2)
            .background(
                Background.of (
                    image = linearGradient(
                        from = rgba(255, 255, 255, 0),
                        to = Colors.White,
                        dir = LinearGradient.Direction.ToRight,
                    ).toImage()
                )
            )

    }
}

// Same as LogosSectionStyle but fills its container width (for use inside narrow columns).
val LogosSectionCompactStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .padding(topBottom = 12.px)
            .whiteSpace(WhiteSpace.NoWrap)
            .position(Position.Relative)
            .overflow(Overflow.Hidden)
    }
    cssRule(":hover div") {
        Modifier.styleModifier {
            property("animation-play-state", "paused")
        }
    }
    before {
        Modifier
            .position(Position.Absolute)
            .top(0.px)
            .left(0.px)
            .width(60.px)
            .height(100.percent)
            .content("")
            .zIndex(2)
            .background(
                Background.of(
                    image = linearGradient(
                        from = rgba(255, 255, 255, 0),
                        to = Colors.White,
                        dir = LinearGradient.Direction.ToLeft,
                    ).toImage()
                )
            )
    }
    after {
        Modifier
            .position(Position.Absolute)
            .top(0.px)
            .right(0.px)
            .width(60.px)
            .height(100.percent)
            .content("")
            .zIndex(2)
            .background(
                Background.of(
                    image = linearGradient(
                        from = rgba(255, 255, 255, 0),
                        to = Colors.White,
                        dir = LinearGradient.Direction.ToRight,
                    ).toImage()
                )
            )
    }
}

val LogosSlideStyle = CssStyle {
    base {
        Modifier
            .width(Width.FitContent)

            .display(DisplayStyle.Flex)
            .flexDirection(FlexDirection.Row)
            .flexWrap(FlexWrap.Nowrap)
            .alignItems(AlignItems.Center)
            .height(80.px)

            .transform { translate3d(0.px,0.px,0.px) }
            .willChange("transform")
    }
}

/* Animations */

val slide = Keyframes {
    from {
        Modifier
            .transform {
                translate3d(0.percent,0.px,0.px)
            }
    }
    to {
        Modifier
            .transform {
                translate3d((-50).percent,0.px,0.px)
            }
    }
}


val slideRight = Keyframes {
    from {
        Modifier
            .transform {
                translate3d((-50).percent,0.px,0.px)
            }
    }
    to {
        Modifier
            .transform {
                translate3d(0.percent,0.px,0.px)
            }
    }
}

val PartnerAnimatedLogoStyle = CssStyle {
    base {
        Modifier
            .width(autoLength)
            .objectFit(ObjectFit.Contain)
            .height(100.percent)
            .margin(leftRight = 40.px)

            .transition(Transition.all(250.ms))
            .textAlign(TextAlign.Center)
            .fontSize(0.8.cssRem)

            .transform { translate3d(0.px,0.px,0.px) }
            .backfaceVisibility(BackfaceVisibility.Hidden)
            .styleModifier {
                property("-webkit-backface-visibility", "hidden")
                property("-webkit-transform", "translate3d(0,0,0)")  // Extra Safari mobile fix
            }
    }
    hover {
        Modifier
            .scale(1.10)
    }
}

@Composable
fun PartnerAnimatedLogo(
    src: String,
    alt: String,
    mod: Modifier = Modifier
) {
    Img(
        src = src,
        alt = alt,
        attrs = PartnerAnimatedLogoStyle.toModifier()
            .then(mod)
            .toAttrs()
    )
}

// Compact slider for use inside narrow containers (e.g. Contact Us right panel).
// Two rows, 45px track height, 18px logo margins — fits within a col-lg-5 column.
@Composable
fun PartnersSectionAnimatedCompact() {
    // Helper: logo with compact margins
    @Composable
    fun Logo(src: String, alt: String, mod: Modifier = Modifier) {
        PartnerAnimatedLogo(src, alt, Modifier.margin(leftRight = 18.px).then(mod))
    }

    // Compact track modifier: 45px height instead of 80px
    val trackMod = Modifier
        .width(Width.FitContent)
        .display(DisplayStyle.Flex)
        .flexDirection(FlexDirection.Row)
        .flexWrap(FlexWrap.Nowrap)
        .alignItems(AlignItems.Center)
        .height(45.px)
        .transform { translate3d(0.px, 0.px, 0.px) }
        .willChange("transform")

    // Row 1 — left scroll
    Div(attrs = LogosSectionCompactStyle.toModifier().toAttrs()) {
        Div(
            attrs = trackMod
                .minWidth(4600.px)
                .animation(
                    slide.toAnimation(
                        duration = Constants.sliderSpeed,
                        iterationCount = AnimationIterationCount.Infinite,
                        timingFunction = AnimationTimingFunction.Linear
                    )
                )
                .willChange("transform")
                .toAttrs()
        ) {
            repeat(2) {
                Logo(src = "/logos/BFGSupplyLogo.png", alt = "BFG Supply Logo")
                Logo(src = "/logos/GrowersChoiceLogo.png", alt = "Growers Choice Logo", mod = Modifier.scale(1.25))
                Logo(src = "/logos/AndenLogo.png", alt = "Anden Logo")
                Logo(src = "/logos/ThinkGrowLogo.png", alt = "Think Grow Logo")
                Logo(src = "/logos/QuestLogo.png", alt = "Quest Logo")
                Logo(src = "/logos/DLWholesaleLogo.jpg", alt = "DL Wholesale Logo")
                Logo(src = "/logos/TrolMasterLogo.webp", alt = "TrolMaster Logo")
                Logo(src = "/logos/MangoTechLogo.png", alt = "Mango Tech Logo")
                Logo(src = "/logos/GKLogo.png", alt = "GK Lighting Logo", mod = Modifier.filter(Filter.of(invert(100.percent))))
                Logo(src = "/logos/BLiteLogo.png", alt = "B Lite Logo")
                Logo(src = "/logos/GavitaLogo.jpeg", alt = "Gavita Logo")
                Logo(src = "/logos/YenjieLogo.jpg", alt = "Yenjie Logo")
                Logo(src = "/logos/FlexstarLogo.png", alt = "Flexstar Logo", mod = Modifier.height(22.px).filter(Filter.of(invert(100.percent))))
            }
        }
    }

    // Row 2 — right scroll
    Div(attrs = LogosSectionCompactStyle.toModifier().toAttrs()) {
        Div(
            attrs = trackMod
                .minWidth(5000.px)
                .animation(
                    slideRight.toAnimation(
                        duration = Constants.sliderSpeed,
                        iterationCount = AnimationIterationCount.Infinite,
                        timingFunction = AnimationTimingFunction.Linear
                    )
                )
                .willChange("transform")
                .toAttrs()
        ) {
            repeat(2) {
                Logo(src = "/logos/HLGLogo.png", alt = "Horticulture Lighting Group Logo", mod = Modifier.height(68.px))
                Logo(src = "/logos/VoltLogo.png", alt = "Volt Logo", mod = Modifier.height(28.px))
                Logo(src = "/logos/GrowprosLogo.png", alt = "GrowPros Logo")
                Logo(src = "/logos/MammothLEDLogo.png", alt = "MammothLED Logo")
                Logo(src = "/logos/FavenLogo.png", alt = "Faven Logo", mod = Modifier.height(31.px))
                Logo(src = "/logos/FloraFlex.png", alt = "FloraFlex Logo")
                Logo(src = "/logos/CLWLogo.png", alt = "California Lightworks Logo")
                Logo(src = "/logos/DaikinLogo.png", alt = "Daikin Logo")
                Logo(src = "/logos/FluenceLogo.png", alt = "Fluence Logo", mod = Modifier.scale(0.8))
                Logo(src = "/logos/IcyLogo.png", alt = "ICYLeds Logo")
                Logo(src = "/logos/LighthouseLogo.png", alt = "Lighthouse Wholesale Logo")
                Logo(src = "/logos/DABPumpsLogo.png", alt = "DAB Pumps Logo")
                Logo(src = "/logos/EnliteLogo.jpg", alt = "Enlite Energy Logo")
            }
        }
    }
}

@Composable
fun PartnersSectionAnimated() {
    // Note - Partners Animated
    Div(LogosSectionStyle.toModifier().toAttrs()) {
        Div(LogosSlideStyle.toModifier()
            .minWidth(7421.13.px)
            .animation(
                slide.toAnimation(
                    duration = Constants.sliderSpeed,
                    iterationCount = AnimationIterationCount.Infinite,
                    timingFunction = AnimationTimingFunction.Linear
                )
            )
            .willChange("transform")
            .toAttrs()
        ) {
            repeat(2) {
                PartnerAnimatedLogo(src = "/logos/BFGSupplyLogo.png", alt = "BFG Supply Logo")
                PartnerAnimatedLogo(src = "/logos/GrowersChoiceLogo.png", alt = "Growers Choice Logo", mod = Modifier.scale(1.25))
                PartnerAnimatedLogo(src = "/logos/AndenLogo.png", alt = "Anden Logo")
                PartnerAnimatedLogo(src = "/logos/ThinkGrowLogo.png", alt = "Think Grow Logo")
                PartnerAnimatedLogo(src = "/logos/QuestLogo.png", alt = "Quest Logo")
                PartnerAnimatedLogo(src = "/logos/DLWholesaleLogo.jpg", alt = "DL Wholesale Logo")
                PartnerAnimatedLogo(src = "/logos/TrolMasterLogo.webp", alt = "TrolMaster Logo")
                PartnerAnimatedLogo(src = "/logos/MangoTechLogo.png", alt = "Mango Tech Logo")
                PartnerAnimatedLogo(src = "/logos/GKLogo.png", alt = "GK Lighting Logo", mod = Modifier.filter(Filter.of(invert(100.percent))))
                PartnerAnimatedLogo(src = "/logos/BLiteLogo.png", alt = "B Lite Logo")
                PartnerAnimatedLogo(src = "/logos/GavitaLogo.jpeg", alt = "Gavita Logo")
                PartnerAnimatedLogo(src = "/logos/YenjieLogo.jpg", alt = "Yenjie Logo")
                PartnerAnimatedLogo(src = "/logos/FlexstarLogo.png", alt = "Flexstar Logo", mod = Modifier.height(35.px).filter(Filter.of(invert(100.percent))))
            }
        }
    }

    // Note - Partners Animated
    Div(LogosSectionStyle.toModifier().toAttrs()) {
        Div(LogosSlideStyle.toModifier()
            .minWidth(8100.px)
            .animation(
                slideRight.toAnimation(
                    duration = Constants.sliderSpeed,
                    iterationCount = AnimationIterationCount.Infinite,
                    timingFunction = AnimationTimingFunction.Linear
                )
            )
            .willChange("transform")
            .toAttrs()
        ) {
            repeat(2) {
                PartnerAnimatedLogo(src = "/logos/HLGLogo.png", alt = "Horticulture Lighting Group Logo", mod = Modifier.height(110.px))
                PartnerAnimatedLogo(src = "/logos/VoltLogo.png", alt = "Volt Logo", mod = Modifier.height(45.px))
                PartnerAnimatedLogo(src = "/logos/GrowprosLogo.png", alt = "GrowPros Logo")
                PartnerAnimatedLogo(src = "/logos/MammothLEDLogo.png", alt = "MammothLED Logo")
                PartnerAnimatedLogo(src = "/logos/FavenLogo.png", alt = "Faven Logo", mod = Modifier.height(50.px))
                PartnerAnimatedLogo(src = "/logos/FloraFlex.png", alt = "FloraFlex Logo")
                PartnerAnimatedLogo(src = "/logos/CLWLogo.png", alt = "California Lightworks Logo")
                PartnerAnimatedLogo(src = "/logos/DaikinLogo.png", alt = "Daikin Logo")
                PartnerAnimatedLogo(src = "/logos/FluenceLogo.png", alt = "Fluence Logo", mod = Modifier.scale(0.8))
                PartnerAnimatedLogo(src = "/logos/IcyLogo.png", alt = "ICYLeds Logo")
                PartnerAnimatedLogo(src = "/logos/LighthouseLogo.png", alt = "Lighthouse Wholesale Logo")
                PartnerAnimatedLogo(src = "/logos/DABPumpsLogo.png", alt = "DAB Pumps Logo")
                PartnerAnimatedLogo(src = "/logos/EnliteLogo.jpg", alt = "Enlite Energy Logo")
            }
        }
    }
}